<script lang="ts">
  // Common Components
  import Button from '../../common/buttons/Button.svelte';
  import UserCard from '../../common/user/UserCard.svelte';
  import Dropdown from '../../common/dropdowns/Dropdown.svelte';
  import Main from '../../common/inputs/textInputs/GlobalTextInput.svelte';
  import { DropdownItem } from '$lib/components/common/dropdowns/dropdown';

  // Components
  import PostPreview from './PostPreview.svelte';
  import Modal from '../SmallModal/Modal.svelte';
  
  // Svelte
  import { createEventDispatcher, onMount } from 'svelte';
  import { fade, fly, slide } from 'svelte/transition';

  // Icons
  import KeyboardHint from '$icons/KeyboardHintIcon.svelte';
  import FolderEmpty from '$icons/PostFolderIcon.svelte';
  import Loader from '$icons/LoaderIcon.svelte';
  import Trash from '$icons/TrashIcon.svelte';

  // Utils
  import { getUserDetails } from '$utils/api/user.util';
  import { findPostById } from '$src/utils/api/posts.util';
  import {
    addItemsToGalleries,
    getMyGalleries,
    type MyGalleriesSingleResponse
  } from '$src/utils/api/gallery.util';

  // Types
  import type { FPost } from 'garden-common';
  import type { IUser } from 'garden-common';

  const dispatch = createEventDispatcher();

  export let selectGallery = null;

  let selectedGalleryItem;
  let selectedGallery;
  let isFetchingGalleries = false;

  let nPostsAdded = 0;
  let nProfilesAdded = 0;

  $: nPostsAdded = renderedItems.filter(e => e.type === 'post').length;
  $: nProfilesAdded = renderedItems.filter(e => e.type === 'talent').length;

  $: if (selectGallery && galleryDropdownItems.length) {
    selectedGalleryItem = galleryDropdownItems.find(g => g.id === selectGallery);
  }

  function closeModal() {
    dispatch('ModalClose');
  }

  $: selectedGallery = selectedGalleryItem?.id;

  let galleries: (MyGalleriesSingleResponse & { selected?: boolean })[] = [];

  async function fetchUserGalleries() {
    isFetchingGalleries = true;
    const res = await getMyGalleries(true);
    galleries = res;
    isFetchingGalleries = false;
  }

  $: galleryDropdownItems = galleries.map(g => {
    return new DropdownItem({ id: g._id, variant: 'label', data: { label: g.title } });
  });

  let isAddingToGallery = false;
  let addToGalleriesErrors: string[] = [];

  $: if (!renderedItems.length) addToGalleriesErrors = [];

  async function addToGalleries() {
    addToGalleriesErrors = [];
    isAddingToGallery = true;

    const res = await addItemsToGalleries(
      renderedItems.map(i => {
        return { type: i.type, _id: i.data._id };
      }),
      [selectedGallery]
    ).catch(err => {
      console.error(err.response);
      addToGalleriesErrors.unshift(err.response.data.message);
      addToGalleriesErrors = addToGalleriesErrors;
    });

    isAddingToGallery = false;
    if (!res) return;

    dispatch('AddedToGalleries');
  }

  let linkInputValue = '';
  let isLinkInputValid = true;
  let isFetchingLink = false;

  $: if (linkInputValue) {
    if (linkInputValue.startsWith('https://')) {
      linkInputValue = linkInputValue.replace('https://', '');
    }

    if (linkInputValue.startsWith('http://')) {
      linkInputValue = linkInputValue.replace('http://', '');
    }

    const isLinkProfile = /^thegarden\.gg\/profile\/[a-zA-Z0-9]+$/g.test(linkInputValue);
    const isLinkPost = /^thegarden\.gg\/post\/[a-zA-Z0-9]{24}$/g.test(linkInputValue);

    isLinkInputValid = !!isLinkPost || !!isLinkProfile;

    let linkType: 'talent' | 'post' = 'talent';

    if (isLinkProfile) {
      linkType = 'talent';
    }

    if (isLinkPost) {
      linkType = 'post';
    }

    if (isLinkInputValid) {
      itemId = linkInputValue.split('/').pop();
      fetchLink(linkType);
    }
  }

  $: if (!linkInputValue) {
    isLinkInputValid = true;
    addLinkErrors = [];
  }

  let addLinkErrors: string[] = [];

  $: itemId = linkInputValue.split('/').pop();

  type RenderItem = { type: 'talent' | 'post'; data: IUser<string> | FPost<string> };

  let currentItem: RenderItem | null = null;
  let renderedItems: RenderItem[] = [];

  async function fetchLink(linkType: 'talent' | 'post') {
    isFetchingLink = true;
    addLinkErrors = [];

    if (linkType === 'talent') {
      const profile = await getUserDetails(itemId).catch(err => {
        console.error(err.response);
        addLinkErrors.unshift(err.response.data.message);
        addLinkErrors = addLinkErrors;
      });

      if (profile) {
        currentItem = { type: linkType, data: profile };
      }
    } else if (linkType === 'post') {
      const post = await findPostById(itemId, 'basic').catch(err => {
        console.error(err.response);
        addLinkErrors.unshift(err.response.data.message);
        addLinkErrors = addLinkErrors;
      });

      if (post) {
        currentItem = { type: linkType, data: post };
      }
    }

    isFetchingLink = false;
  }

  function addToRenderedItems() {
    if (renderedItems.some(e => e.data._id === currentItem.data._id)) {
      addLinkErrors.unshift('This item has already been selected');
      addLinkErrors = addLinkErrors;
      return;
    }

    renderedItems.unshift(currentItem);
    renderedItems = renderedItems;
    linkInputValue = '';
  }

  $: isNextDisabled =
    nPostsAdded < 1 || nProfilesAdded < 1 || isFetchingGalleries || isAddingToGallery;
  $: isAddDisabled = !isLinkInputValid || isFetchingLink || addLinkErrors.length > 0 || !itemId;
  $: isLinkInputDisabled = isFetchingLink;

  onMount(fetchUserGalleries);
</script>

<Modal class="min-w-[600px] h-[800px]" contentOverflow on:ModalClose={closeModal}>
  <!-- Header -->
  <div slot="header" class="flex flex-col items-center gap-8 mt-12 mb-6">
    <FolderEmpty class="stroke-brand-green" size={'64'} />

    <div class="flex flex-col items-center gap-2">
      <h1 class="text-2xl font-bold">Add to your gallery</h1>

      <h2 class="text-grey-transparency-dark text-opacity-40 text-sm">
        Add any post or profile to your gallery to create your unique curations
      </h2>
    </div>
  </div>

  <!-- Content -->
  <div
    slot="content"
    class=" overflow-y-scroll overflow-x-hidden h-full px-4 py-6 border border-blueish-grey-200 rounded-2xl"
    data-simplebar
    data-simplebar-auto-hide="false"
  >
    <div class="flex flex-col gap-8">
      {#if galleryDropdownItems.length > 0}
        <div in:fly|global={{ y: -50 }} class="">
          <Dropdown
            bind:selected={selectedGalleryItem}
            label="Gallery"
            variant="border-selected-label-with-arrow"
            variantOptions={{
              extLabelClasses: '!text-garden-grey-600',
              arrowColor: '#47675B',
              variant: 'border-selected-label-with-arrow'
            }}
            items={galleryDropdownItems}
          />
        </div>
      {/if}

      <div class="flex flex-col gap-2 w-full text-left">
        <div class="flex items-center gap-4 w-full">
          <div class="relative w-full flex flex-row justify-baseline items-center">
            <Main
              bind:value={linkInputValue}
              danger={!isLinkInputValid}
              dangerMessage="Invalid link provided"
              label="Link"
              caption="Paste the link of a post or profile from The Garden to add to your gallery."
              disabled={isLinkInputDisabled}
            />

            {#if isFetchingLink}
              <div class="absolute top-[2.75rem] right-4" in:fly|global={{ x: -50, duration: 500 }}>
                <Loader />
              </div>
            {/if}
          </div>

          <Button
            label="Add"
            disabled={isAddDisabled}
            class="h-[58px]"
            on:click={addToRenderedItems}
          />
        </div>

        {#each addLinkErrors as errorMessage}
          <span in:slide|global class="text-red-500 text-xs">{errorMessage}</span>
        {/each}

        {#each addToGalleriesErrors as errorMessage}
          <span in:slide|global class="text-red-500 text-xs">{errorMessage}</span>
        {/each}

        {#if renderedItems.length}
          <div
            class="flex overflow-y-visible overflow-x-scroll gap-8 pt-8 max-h-[30rem]"
            transition:fly|global={{ y: -100 }}
          >
            {#each renderedItems as item}
              {#if item.type === 'post'}
                {@const post = {
                  _id: item.data._id,
                  /* @ts-ignore */
                  creator: item.data.owner,
                  /* @ts-ignore */
                  imageUrl: item.data.media
                    .find(e => e.thumbnails.some(e => e))
                    ?.thumbnails.find(e => e)
                }}

                <div class="relative min-w-[24rem]">
                  <PostPreview {post} />

                  <button
                    on:click={() => (renderedItems = renderedItems.filter(e => e !== item))}
                    class="absolute -top-4 -right-4 w-10 h-10 bg-red-200 rounded-full grid place-items-center outline outline-1 outline-blueish-grey-200 active:translate-y-1 transition"
                  >
                    <Trash size="24" class="stroke-accent-red-50" />
                  </button>
                </div>
              {:else if item.type === 'talent'}
                <div class="relative min-w-[24rem]">
                  <UserCard user={item.data} displayFollow={false} />

                  <button
                    on:click={() => (renderedItems = renderedItems.filter(e => e !== item))}
                    class="absolute -top-4 -right-4 w-10 h-10 bg-red-200 rounded-full grid place-items-center outline outline-1 outline-blueish-grey-200 active:translate-y-1 transition"
                  >
                    <Trash size="24" class="stroke-accent-red-50" />
                  </button>
                </div>
              {/if}
            {/each}
          </div>
        {/if}
      </div>
    </div>
  </div>

  <!-- Footer -->
  <div slot="footer">
    <div class="flex px-2 items-center gap-2 mt-4 text-gray-400 flex-grow">
      {#if nPostsAdded}
        <div transition:fade|global>
          {nPostsAdded}
          {nPostsAdded === 1 ? 'post' : 'posts'} selected
        </div>
      {/if}

      {#if nPostsAdded && nProfilesAdded}
        <span class="" />
      {/if}

      {#if nProfilesAdded}
        <div transition:fade|global>
          {nProfilesAdded}
          {nProfilesAdded === 1 ? 'profile' : 'profiles'} selected
        </div>
      {/if}

      <div class="flex-grow" />

      <div class="flex items-center gap-2">
        <Button secondary clickOnKey="Escape" label="Cancel" on:click={closeModal}>
          <div slot="icon-right">
            <KeyboardHint type="escape" />
          </div>
        </Button>

        <Button
          primary
          border={false}
          clickOnKey="Enter"
          iconRight={KeyboardHint}
          label="Add to gallery"
          disabled={isNextDisabled}
          on:click={addToGalleries}
        />
      </div>

      {#if isAddingToGallery}
        <div in:fly|global={{ x: 20 }}>
          <Loader />
        </div>
      {/if}
    </div>
  </div>
</Modal>

<style lang="postcss">
  :global(.simplebar-scrollbar::before) {
    @apply my-2 translate-x-[5px];
    background: #d0ddd7 !important;
    width: 4px;
  }

  :global(.simplebar-content-wrapper) {
    @apply overscroll-contain;
  }
</style>
